import { seafoodItems } from "../data/seafood";
import { LEGACY_PACK, isPackId, type PackId } from "../data/packs";

const STORAGE_KEY = "zenfresh_cart_v1";

/** One cart line: a catch in one pack size. The same catch in two sizes is two lines. */
export interface CartLineItem {
  id: string;
  pack: PackId;
  /** Number of packs (not kg). */
  qty: number;
  /** The chosen cut (data/cuts.ts CutId); "" = the catch's default. One cut applies to every pack of a catch. */
  cut: string;
}

/**
 * Drops any id no longer in the live catalog and any non-positive qty, so a stale cart from a prior deploy can never resolve to
 * a broken line item. A line saved before pack sizes existed (quantities were in kg) is read as that many 1 kg packs.
 */
export function readCart(): CartLineItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { id?: unknown; pack?: unknown; qty?: unknown; cut?: unknown }[];
    if (!Array.isArray(parsed)) return [];
    const validIds = new Set(seafoodItems.map((item) => item.id));
    return parsed
      .filter((line) => typeof line.id === "string" && validIds.has(line.id) && typeof line.qty === "number" && line.qty > 0)
      .map((line) => ({
        id: line.id as string,
        pack: isPackId(line.pack) ? line.pack : LEGACY_PACK,
        qty: Math.floor(line.qty as number),
        cut: typeof line.cut === "string" ? line.cut : "",
      }));
  } catch {
    return [];
  }
}

export function writeCart(items: CartLineItem[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Storage unavailable (private browsing, quota, etc.) — cart simply won't persist across visits.
  }
}

export function clearCartStorage() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do if storage is unavailable.
  }
}
